import * as borogove from "borogove";
import { Button, Search, Sidebar } from "@chatscope/chat-ui-kit-react";
import { CircularProgress, Dialog } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router";
import Linkify from "linkify-react";
import { useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import UnsplashPicker, { InsertIntoApplicationUploader } from "unsplash-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faThumbtack,
	faThumbtackSlash,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { Profile } from "./Profile";

export const ChatDetails = () => {
	const navigate = useNavigate();
	const [showChooseBackground, setShowChooseBackground] = useState(false);
	const {
		activeChat,
		client,
		setPopupMember,
		selectContact,
		setWallpaper,
		persistence,
	} = useOutletContext<{
		activeChat: borogove.Chat;
		client: borogove.Client;
		setPopupMember: (member: borogove.Member) => void;
		selectContact: (f: (contactChat: borogove.Chat) => void) => void;
		setWallpaper: (url: string) => void;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
	}>();

	let activeChatNotifications: "all" | "mute" | "mention" | "reply" = "all";
	if (activeChat) {
		if (activeChat.notificationsFiltered()) {
			if (activeChat.notifyReply()) {
				activeChatNotifications = "reply";
			} else if (activeChat.notifyMention()) {
				activeChatNotifications = "mention";
			} else {
				activeChatNotifications = "mute";
			}
		}
	}

	if (!activeChat) return <div />;

	return (
		<Sidebar position="right" scrollable={false} className={"active"}>
			<Button
				icon={<FontAwesomeIcon icon={faXmark} />}
				onClick={() => navigate(location.pathname.replace(/\/details$/, ""))}
				style={{ float: "right" }}
				title="Close sidebar"
			/>
			<h2>Chat details</h2>
			<code>{activeChat.chatId}</code>

			{(activeChat.status.emoji || activeChat.status.text) && (
				<p className="status" title="Status">
					<span className="emoji">{activeChat.status.emoji}</span>
					<span className="text">{activeChat.status.text}</span>
				</p>
			)}

			{activeChat instanceof borogove.Channel && (
				<p className="description">
					<Linkify options={{ defaultProtocol: "https", truncate: 37 }}>
						{activeChat.description()}
					</Linkify>
				</p>
			)}

			<section>
				<h3>Settings</h3>
				<Button
					icon={
						<FontAwesomeIcon
							icon={
								activeChat.uiState === borogove.UiState.Pinned
									? faThumbtackSlash
									: faThumbtack
							}
						/>
					}
					onClick={() => activeChat.togglePinned()}
				>
					{activeChat.uiState === borogove.UiState.Pinned
						? "Unpin Chat"
						: "Pin Chat"}
				</Button>
				<Button onClick={() => activeChat.close()}>
					{activeChat instanceof borogove.Channel
						? "Leave Chat"
						: "Archive Chat"}
				</Button>
				{activeChat.canInvite() && (
					<Button
						onClick={() =>
							selectContact((contactChat) => activeChat.invite(contactChat))
						}
					>
						Invite new Member
					</Button>
				)}
				<label>
					<span>Notifications</span>
					<select
						onChange={(e) => {
							if (e.target.value === "all") {
								activeChat.setNotifications(false, true, true);
							} else if (e.target.value === "mute") {
								activeChat.setNotifications(true, false, false);
							} else if (e.target.value === "mention") {
								activeChat.setNotifications(true, true, false);
							} else if (e.target.value === "reply") {
								activeChat.setNotifications(true, true, true);
							}
						}}
					>
						<option value="all" selected={activeChatNotifications === "all"}>
							All Messages
						</option>
						<option
							value="mention"
							selected={activeChatNotifications === "mention"}
						>
							Mentions Only
						</option>
						<option
							value="reply"
							selected={activeChatNotifications === "reply"}
						>
							Mentions and Replies Only
						</option>
						<option value="mute" selected={activeChatNotifications === "mute"}>
							Mute
						</option>
					</select>
				</label>
				{activeChat.isBlocked && (
					<Button onClick={() => activeChat.unblock(true)}>Unblock</Button>
				)}
				{!activeChat.isBlocked && (
					<Button onClick={() => activeChat.block()}>Block</Button>
				)}
				{!activeChat.isBlocked && (
					<Button
						onClick={() => activeChat.block(true, activeChat.lastMessage)}
					>
						Report Spam
					</Button>
				)}
				<Button onClick={() => setShowChooseBackground(true)}>
					Set Background
				</Button>

				<AwaitOnce
					key={activeChat.chatId}
					resolve={() => activeChat.members()}
					fallback={<CircularProgress />}
				>
					{(members) => <MemberList client={client} setPopupMember={setPopupMember} chat={activeChat} members={members} />}
				</AwaitOnce>
			</section>
			<Dialog
				open={showChooseBackground}
				onClose={() => setShowChooseBackground(false)}
				fullWidth={true}
			>
				<div
					style={{
						height:
							window.innerWidth < 600
								? (window.innerWidth * 4) / 3 + "px"
								: "700px",
						maxHeight: "80vh",
						overflowY: "auto",
					}}
				>
					<UnsplashPicker
						applicationName={import.meta.env.VITE_APP_TITLE}
						accessKey={import.meta.env.VITE_UNSPLASH_ACCESS_KEY}
						preferredSize={{ width: screen.width, height: screen.height }}
						Uploader={InsertIntoApplicationUploader}
						defaultSearch="illustration abstract texture"
						onFinishedUploading={(inurl) => {
							const url =
								inurl.replace(/(?:fm|crop|cs|q)=[^&]+&?/g, "") +
								"&auto=format&fit=crop&crop=entropy&q=70";
							persistence.set(`ui#wallpaper/${activeChat?.chatId}`, url);
							setWallpaper(url);
							setShowChooseBackground(false);
						}}
					/>
				</div>
			</Dialog>
		</Sidebar>
	);
};

export const MemberList = ({
	client,
	chat,
	setPopupMember,
	members: initialMembers,
}: {
	client: borogove.Client;
	chat: borogove.Chat;
	setPopupMember: (member: borogove.Member) => void;
	members: borogove.Member[];
}) => {
	const [memberSearchQ, setMemberSearchQ] = useQueryState("memberQ");
	const [members, setMembers] = useState(initialMembers);
	useEffect(() => {
		const token = chat.addMembersUpdatedListener((updated) => {
			setMembers((existing) => {
				const updates = new Map(updated.map((m) => [m.id, m]));

				const replaced = existing.map((m) =>
					updates.get(m.id) ?? m,
				);

				// Append new items for now instead of re-sorting
				const haveIds = new Set(existing.map((m) => m.id));
				const appended = updated.filter((m) => !haveIds.has(m.id));

				return [...replaced, ...appended];
			});
		});

		return () => chat.removeEventListener(token);
	}, [chat]);

	return members.length > 2 ? (
		<>
			<h3>Members</h3>
			{members.length > 15 && (
				<Search
					placeholder="Search by name, ID"
					value={memberSearchQ || ""}
					onChange={(q) => setMemberSearchQ(q || null)}
					onClearClick={() => setMemberSearchQ(null)}
				/>
			)}
			<ul className="members">
				{members
					.filter(
						(member) =>
							!memberSearchQ ||
							member.displayName.includes(memberSearchQ) ||
							member.chat?.chatId?.includes(memberSearchQ),
					)
					.map((member) => (
						<li key={`member-${member.id}`}>
							<Button onClick={() => setPopupMember(member)}>
								<AvatarWithPlaceholder
									photoUri={member.photoUri}
									placeholderUri={member.placeholderUri}
									size="sm"
								/>
								{member.displayName}
							</Button>
						</li>
					))}
			</ul>
		</>
	) : (
		<>
			<h3>Profile</h3>
			{members
				.filter((p) => !p.isSelf)
				.map((p) => (
					<AwaitOnce
						key={chat.chatId}
						resolve={() => p.profile(client)}
						fallback={<CircularProgress />}
					>
						{(profile) => <Profile profile={profile} />}
					</AwaitOnce>
				))}
		</>
	);
};
